import Router from "next/router";
import { setLoadingBar } from "@/redux/siteData";
import { setUserData, setLoading } from "@/redux/signIn";
import { api } from "@/services/api";

export interface SignInActionCallbacks {
  onSuccess: (userData: any) => void;
  onError?: () => void;
  onUserDoesNotExist?: (params: {
    accountType: "google" | "facebook" | "apple";
    credential?: string;
    token?: string;
  }) => void;
}

/**
 * Sign in with username/password
 */
export function signInWithCredentials(
  username: string,
  password: string,
  locale: string,
  callbacks: SignInActionCallbacks,
  premium?: string
) {
  setLoadingBar(true);
  api
    .post("/auth/signin/" + locale, { username, password, premium })
    .then((response) => {
      if (response.status >= 400) {
        throw response.status;
      }
      setUserData(response.data);
      callbacks.onSuccess(response.data);
    })
    .catch(() => {
      callbacks.onError?.();
    })
    .then(() => {
      setLoadingBar(false);
    });
}

/**
 * Sign in with Google credential. On 204 (user doesn't exist), calls onUserDoesNotExist.
 */
export function signInWithGoogle(
  credential: string,
  locale: string,
  callbacks: SignInActionCallbacks,
  premium?: string
) {
  setLoadingBar(true);
  api
    .post("/auth/google/token", { credential, premium })
    .then((response) => {
      if (response.status === 200 && response.data) {
        setUserData(response.data);
        setLoading(false);
        callbacks.onSuccess(response.data);
      } else if (response.status === 204) {
        callbacks.onUserDoesNotExist?.({
          accountType: "google",
          credential,
        });
      }
    })
    .catch(() => {
      setLoading(false);
      callbacks.onError?.();
    })
    .then(() => {
      setLoadingBar(false);
    });
}

/**
 * Sign in with Facebook access token. On willCreateFacebookUser, calls onUserDoesNotExist.
 */
export function signInWithFB(
  accessToken: string,
  callbacks: SignInActionCallbacks
) {
  setLoadingBar(true);
  api
    .post("/auth/facebook/token", { access_token: accessToken })
    .then((response) => {
      if (!response?.data) return;
      if (response.data.willCreateFacebookUser) {
        callbacks.onUserDoesNotExist?.({
          accountType: "facebook",
          token: accessToken,
        });
      } else {
        setUserData(response.data);
        setLoading(false);
        callbacks.onSuccess(response.data);
      }
    })
    .catch(() => {
      setLoading(false);
      callbacks.onError?.();
    })
    .then(() => {
      setLoadingBar(false);
    });
}

/**
 * Initiates Facebook login and then sign-in
 */
export function signInFBButton(callbacks: SignInActionCallbacks) {
  if (typeof window !== "undefined" && (window as any).FB) {
    setLoadingBar(true);
    (window as any).FB.login(
      (response: any) => {
        if (response.status === "connected") {
          signInWithFB(response.authResponse.accessToken, callbacks);
        } else {
          setLoadingBar(false);
        }
      },
      { scope: "gaming_profile" }
    );
  } else {
    setLoadingBar(true);
    setTimeout(() => signInFBButton(callbacks), 100);
  }
}

/**
 * Sign in with Apple id_token. On willCreateAppleUser, calls onUserDoesNotExist.
 */
export function signInWithApple(
  token: string,
  callbacks: SignInActionCallbacks
) {
  setLoadingBar(true);
  api
    .post("/auth/apple/token", { token })
    .then((response) => {
      if (!response?.data) return;
      if (response.data.willCreateAppleUser) {
        callbacks.onUserDoesNotExist?.({
          accountType: "apple",
          token,
        });
      } else {
        setUserData(response.data);
        setLoading(false);
        callbacks.onSuccess(response.data);
      }
    })
    .catch(() => {
      setLoading(false);
      callbacks.onError?.();
    })
    .then(() => {
      setLoadingBar(false);
    });
}

/**
 * Initiates Apple sign-in and then sign-in
 */
export async function signInAppleButton(callbacks: SignInActionCallbacks) {
  setLoadingBar(true);
  try {
    const data = await (window as any).AppleID.auth.signIn();
    if (data.authorization) {
      signInWithApple(data.authorization.id_token, callbacks);
    } else {
      setLoadingBar(false);
    }
  } catch {
    setLoadingBar(false);
  }
}

/**
 * Navigate to register when user doesn't exist (used from popup). Closes popup and redirects.
 */
export function redirectToRegisterForNewUser(
  params: { accountType: "google" | "facebook" | "apple"; credential?: string; token?: string },
  onClose: () => void
) {
  onClose();
  if (params.accountType === "google" && params.credential) {
    Router.push(`/register?accountType=google&credential=${params.credential}`);
  } else if (params.accountType === "facebook" && params.token) {
    Router.push(`/register/addUsername?accountType=facebook&token=${params.token}`);
  } else if (params.accountType === "apple" && params.token) {
    Router.push(`/register/addUsername?accountType=apple&token=${params.token}`);
  } else {
    Router.push("/register");
  }
}

/**
 * Initialize Google Sign-In button for sign-in (not register). Uses "signin_with" text.
 */
export function initializeGoogleButtonForSignIn(
  locale: string,
  callbacks: SignInActionCallbacks,
  premium?: string
) {
  const g = (window as any).google;
  if (!g?.accounts?.id) return;

  const clientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
  if (!clientId) return;

  g.accounts.id.initialize({
    client_id: clientId,
    callback: (res: any) => {
      signInWithGoogle(res.credential, locale, callbacks, premium);
    },
    use_fedcm_for_prompt: true,
  });

  const gbtn = document.getElementById("signin-popup-gbtn");
  let buttonWidth = 0;
  if (typeof window !== "undefined") {
    if (window.innerWidth > 480) {
      buttonWidth = Math.min(400, window.innerWidth - 42);
    } else {
      buttonWidth = window.innerWidth - 82;
    }
  }

  if (gbtn) {
    gbtn.innerHTML = "";
    g.accounts.id.renderButton(gbtn, {
      theme: "outline",
      size: "large",
      width: buttonWidth,
      longtitle: true,
      text: "signin_with",
      logo_alignment: "left",
    });
  }
}
